import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { CalendarClock, CreditCard } from "lucide-react";

import { scholarshipService, type AwardStatus, type ScholarshipAward, type ScholarshipPayment, type ScholarshipRenewal } from "../../services/scholarshipService";
import { apiErrorMessage } from "../../utils/apiError";
import { Field } from "../FormLayout";
import { ReasonPanel } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { PaymentForm, RenewalForm } from "./ScholarshipForms";
import { awardConfig, awardStatus, awardTransitions, day, methodLabel, paidShare, payable, paymentConfig, renewalConfig, renewalStatus, ugx } from "./scholarshipLogic";

/** Every award, what has been paid out of it and what is left. */
export function AwardsPanel({ rows, payments, renewals, canManage, isLoading, isError, onRetry, onChanged, initialView }: {
  rows?: ScholarshipAward[];
  payments: ScholarshipPayment[];
  renewals: ScholarshipRenewal[];
  canManage: boolean;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
  initialView?: string;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [form, setForm] = useState<"payment" | "renewal" | null>(null);
  const done = (text: string) => { setForm(null); setNotice({ tone: "success", text }); onChanged(); };
  const transition = useMutation({
    mutationFn: scholarshipService.transitionAward,
    onSuccess: (saved) => { setViewing(null); done(`${saved.application_name}'s award: ${awardStatus(saved.status).label.toLowerCase()}.`); },
  });
  const pay = useMutation({
    mutationFn: scholarshipService.createPayment,
    onSuccess: (saved) => done(`${ugx(saved.amount)} paid out to ${saved.award_name}.`),
  });
  const renew = useMutation({
    mutationFn: scholarshipService.createRenewal,
    onSuccess: (saved) => done(`Renewal for ${saved.award_name} sent for a decision.`),
  });

  const columns: RegisterColumn<ScholarshipAward>[] = [
    { header: "Learner", cell: (row) => <span><span className="font-medium text-gray-900">{row.application_name}</span> <span className="text-xs text-gray-500">{row.student_number}</span></span>, exportValue: (row) => row.application_name },
    { header: "Student ID", cell: (row) => row.student_number ?? "", exportValue: (row) => row.student_number ?? "", exportOnly: true },
    { header: "Class", cell: (row) => row.class_name || "—", exportValue: (row) => row.class_name ?? "", exportOnly: true },
    { header: "Scholarship", cell: (row) => row.scholarship_name, exportValue: (row) => row.scholarship_name },
    { header: "Awarded", cell: (row) => ugx(row.award_amount), exportValue: (row) => Number(row.award_amount), align: "right" },
    { header: "Paid out", cell: (row) => ugx(row.paid_total), exportValue: (row) => Number(row.paid_total ?? 0), align: "right" },
    { header: "Left", cell: (row) => ugx(row.balance), exportValue: (row) => Number(row.balance ?? 0), align: "right" },
    { header: "Runs", cell: (row) => `${day(row.effective_date)} → ${day(row.expiry_date) || "open"}`, exportValue: (row) => `${day(row.effective_date)} to ${day(row.expiry_date) || "open"}` },
    { header: "Status", cell: (row) => <StatusDot {...awardStatus(row.status)} />, exportValue: (row) => awardStatus(row.status).label },
  ];
  const row = viewing ? rows?.find((item) => item.id === viewing) : undefined;
  const rowPayments = row ? payments.filter((item) => item.award === row.id) : [];
  const rowRenewals = row ? renewals.filter((item) => item.award === row.id) : [];
  const canPay = row ? payable(row) : undefined;
  const pendingRenewal = rowRenewals.some((item) => item.status === "pending");

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        title={canManage ? "Awards" : "Your awards"}
        description={canManage ? "Each learner on a scholarship: the award, what has been paid out of it and what is left. Open one to pay out, review it for renewal, or suspend or end it with a reason." : "Scholarships awarded to your children, and what has been paid out."}
        rows={rows}
        config={awardConfig}
        initialState={initialView ? { view: initialView } : undefined}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        openLabel={() => (canManage ? "Manage" : "Open")}
        noun={["award", "awards"]}
        exportName={{ file: "scholarship-awards", title: "Scholarship Awards" }}
        canExport={canManage}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No awards yet. Approved applications are awarded from the Applications tab."
      />

      {row && !form && (
        <DetailsModal
          size="3xl"
          title={row.application_name}
          headline={{ label: row.scholarship_name, value: ugx(row.award_amount), note: `${ugx(row.paid_total)} paid out · ${ugx(row.balance)} left` }}
          status={awardStatus(row.status)}
          notice={
            <div className="h-2 overflow-hidden rounded-full bg-gray-100" role="img" aria-label={`${paidShare(row)}% paid out`}>
              <div className="h-full rounded-full bg-emerald-500" style={{ width: `${paidShare(row)}%` }} />
            </div>
          }
          facts={[
            ["Student ID", row.student_number ?? "—"],
            ["Class", row.class_name || "—"],
            ["Awarded on", day(row.award_date)],
            ["Runs", `${day(row.effective_date)} → ${day(row.expiry_date) || "no end date"}`],
            ["Awarded by", row.awarded_by_name || "—"],
          ]}
          sections={[{ title: "To keep it", body: row.renewal_requirements }, { title: "Terms", body: row.terms_conditions }, { title: "History", body: row.notes }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && (row.status === "active" || row.status === "suspended") && (
                <button type="button" className="btn btn-outline gap-1.5" disabled={pendingRenewal} title={pendingRenewal ? "A renewal is already waiting for a decision" : undefined} onClick={() => { renew.reset(); setForm("renewal"); }}>
                  <CalendarClock className="h-4 w-4" /> Review for renewal
                </button>
              )}
              {canManage && (
                <button type="button" className="btn btn-primary gap-1.5" disabled={!canPay?.amount} title={canPay?.reason || undefined} onClick={() => { pay.reset(); setForm("payment"); }}>
                  <CreditCard className="h-4 w-4" /> Record disbursement
                </button>
              )}
            </>
          }
        >
          <section className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Disbursements</h4>
            {rowPayments.length ? (
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 text-sm">
                {rowPayments.map((item) => (
                  <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
                    <span>{day(item.payment_date)} · {methodLabel(item.payment_method)} · <span className="text-gray-500">{item.reference_number}</span></span>
                    <span className="font-semibold tabular-nums">{ugx(item.amount)}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm text-gray-500">Nothing paid out yet.</p>}
          </section>
          {rowRenewals.length > 0 && (
            <section className="space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Renewals</h4>
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 text-sm">
                {rowRenewals.map((item) => (
                  <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
                    <span>{item.renewal_period} · until {day(item.new_expiry_date)}</span>
                    <StatusDot {...renewalStatus(item.status)} />
                  </li>
                ))}
              </ul>
            </section>
          )}
          {canManage && <AwardStatusPanel award={row} busy={transition.isPending} error={transition.error} onChange={(status, reason) => transition.mutate({ id: row.id, status, reason })} />}
        </DetailsModal>
      )}

      {row && form === "payment" && <PaymentForm award={row} busy={pay.isPending} error={pay.isError ? apiErrorMessage(pay.error, "The disbursement could not be recorded.") : undefined} onSave={(data) => pay.mutate(data)} onClose={() => setForm(null)} />}
      {row && form === "renewal" && <RenewalForm award={row} busy={renew.isPending} error={renew.isError ? apiErrorMessage(renew.error, "The renewal could not be sent.") : undefined} onSave={(data) => renew.mutate(data)} onClose={() => setForm(null)} />}
    </div>
  );
}

function AwardStatusPanel({ award, busy, error, onChange }: { award: ScholarshipAward; busy: boolean; error: unknown; onChange: (status: AwardStatus, reason: string) => void }) {
  const options = awardTransitions(award.status);
  const [target, setTarget] = useState<AwardStatus | undefined>(options[0]?.value);
  if (!options.length || !target) return null;
  const chosen = options.find((option) => option.value === target);
  return (
    <ReasonPanel
      title="Change the award"
      description={chosen?.help}
      label="Reason"
      placeholder="What the change rests on; the family sees it"
      context={{ key: "scholarship.note", params: { kind: "award_status", id: award.id, decision: target } }}
      button={chosen?.label ?? "Save"}
      danger={target === "terminated"}
      busy={busy}
      error={error}
      errorText="The award could not be changed."
      onSubmit={(reason) => (target !== "terminated" || window.confirm("End this award for good?")) && onChange(target, reason)}
    >
      <Field label="Change to">
        <select value={target} onChange={(event) => setTarget(event.target.value as AwardStatus)} className="input mt-1">{options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select>
      </Field>
    </ReasonPanel>
  );
}

/** Money paid out of awards: the disbursement register. */
export function PaymentsPanel({ rows, isLoading, isError, onRetry }: { rows?: ScholarshipPayment[]; isLoading: boolean; isError: boolean; onRetry: () => void }) {
  const [viewing, setViewing] = useState<number | null>(null);
  const columns: RegisterColumn<ScholarshipPayment>[] = [
    { header: "Paid on", cell: (row) => day(row.payment_date), exportValue: (row) => day(row.payment_date) },
    { header: "Learner", cell: (row) => <span><span className="font-medium text-gray-900">{row.award_name}</span> <span className="text-xs text-gray-500">{row.student_number}</span></span>, exportValue: (row) => row.award_name },
    { header: "Scholarship", cell: (row) => row.scholarship_name ?? "", exportValue: (row) => row.scholarship_name ?? "" },
    { header: "How", cell: (row) => methodLabel(row.payment_method), exportValue: (row) => methodLabel(row.payment_method) },
    { header: "Reference", cell: (row) => row.reference_number, exportValue: (row) => row.reference_number },
    { header: "Amount", cell: (row) => ugx(row.amount), exportValue: (row) => Number(row.amount), align: "right" },
    { header: "Recorded by", cell: (row) => row.processed_by_name, exportValue: (row) => row.processed_by_name, exportOnly: true },
  ];
  const row = viewing ? rows?.find((item) => item.id === viewing) : undefined;
  const total = (rows ?? []).reduce((sum, item) => sum + Number(item.amount), 0);
  return (
    <>
      <ClientRegister
        title="Disbursements"
        description={`Every payment out of a scholarship award${rows?.length ? `: ${ugx(total)} in all` : ""}. To pay out, open the award.`}
        rows={rows}
        config={paymentConfig}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        noun={["disbursement", "disbursements"]}
        exportName={{ file: "scholarship-disbursements", title: "Scholarship Disbursements" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="Nothing has been paid out yet."
      />
      {row && (
        <DetailsModal
          title={row.award_name}
          headline={{ label: row.scholarship_name ?? "Disbursement", value: ugx(row.amount), note: `Paid ${day(row.payment_date)}` }}
          facts={[["How", methodLabel(row.payment_method)], ["Reference", row.reference_number], ["Recorded by", row.processed_by_name], ["Student ID", row.student_number ?? "—"]]}
          sections={[{ title: "Notes", body: row.notes }]}
          onClose={() => setViewing(null)}
        />
      )}
    </>
  );
}

/** Renewal reviews waiting for a decision, and past ones. */
export function RenewalsPanel({ rows, isLoading, isError, onRetry, onChanged }: { rows?: ScholarshipRenewal[]; isLoading: boolean; isError: boolean; onRetry: () => void; onChanged: () => void }) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [decision, setDecision] = useState<"approved" | "conditional" | "rejected">("approved");
  const review = useMutation({
    mutationFn: scholarshipService.reviewRenewal,
    onSuccess: (saved) => { setViewing(null); setNotice({ tone: "success", text: `${saved.award_name}: ${renewalStatus(saved.status).label.toLowerCase()}.` }); onChanged(); },
  });
  const columns: RegisterColumn<ScholarshipRenewal>[] = [
    { header: "Learner", cell: (row) => <span className="font-medium text-gray-900">{row.award_name}</span>, exportValue: (row) => row.award_name },
    { header: "Scholarship", cell: (row) => row.scholarship_name ?? "", exportValue: (row) => row.scholarship_name ?? "" },
    { header: "For", cell: (row) => row.renewal_period, exportValue: (row) => row.renewal_period },
    { header: "Until", cell: (row) => day(row.new_expiry_date), exportValue: (row) => day(row.new_expiry_date) },
    { header: "Attendance", cell: (row) => (row.attendance_rate ? `${Number(row.attendance_rate)}%` : "—"), exportValue: (row) => row.attendance_rate ?? "", align: "right" },
    { header: "GPA", cell: (row) => row.current_gpa ?? "—", exportValue: (row) => row.current_gpa ?? "", exportOnly: true },
    { header: "Status", cell: (row) => <StatusDot {...renewalStatus(row.status)} />, exportValue: (row) => renewalStatus(row.status).label },
  ];
  const row = viewing ? rows?.find((item) => item.id === viewing) : undefined;
  const help = { approved: "The award runs on to the new date and is made active again.", conditional: "Renewed, with conditions you set out in the reason.", rejected: "The award keeps its current end date." }[decision];
  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Renewals"
        description="Awards reviewed to run on for another period. Start a review from the award; decide it here."
        rows={rows}
        config={renewalConfig}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => { setDecision("approved"); setViewing(item.id); }}
        openLabel={(item) => (item.status === "pending" ? "Decide" : "Open")}
        noun={["renewal", "renewals"]}
        exportName={{ file: "scholarship-renewals", title: "Scholarship Renewals" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No renewals yet."
      />
      {row && (
        <DetailsModal
          title={row.award_name}
          headline={{ label: row.scholarship_name ?? "Renewal", value: row.renewal_period, note: `Would run until ${day(row.new_expiry_date)}${row.award_expiry_date ? ` (now ${day(row.award_expiry_date)})` : ""}` }}
          status={renewalStatus(row.status)}
          facts={[["Reviewed for", day(row.renewal_date)], ["Attendance", row.attendance_rate ? `${Number(row.attendance_rate)}%` : "—"], ["GPA", row.current_gpa ?? "—"], ["Decided by", row.reviewed_by_name || "—"]]}
          sections={[{ title: "Notes", body: row.notes }, { title: "Decision", body: row.review_notes, tone: row.status === "rejected" ? "danger" : "plain" }]}
          onClose={() => setViewing(null)}
        >
          {row.status === "pending" && (
            <ReasonPanel title="Decide the renewal" description={help} label="Reason" placeholder="What the decision rests on" context={{ key: "scholarship.note", params: { kind: "renewal", id: row.id, decision } }} button={{ approved: "Renew", conditional: "Renew on conditions", rejected: "Do not renew" }[decision]} danger={decision === "rejected"} busy={review.isPending} error={review.error} errorText="The decision could not be recorded." onSubmit={(notes) => review.mutate({ id: row.id, status: decision, notes })}>
              <Field label="Decision">
                <select value={decision} onChange={(event) => setDecision(event.target.value as typeof decision)} className="input mt-1">
                  <option value="approved">Renew</option>
                  <option value="conditional">Renew on conditions</option>
                  <option value="rejected">Do not renew</option>
                </select>
              </Field>
            </ReasonPanel>
          )}
        </DetailsModal>
      )}
    </div>
  );
}
